import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  Paper,
  Button,
  Stack,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  ProductionQuantityLimits,
  Build,
  CheckCircle,
  DeliveryDining,
  PrecisionManufacturing,
  Storage,
  Settings,
  AutoAwesome,
  Speed,
  Co2Outlined,
  Verified,
  Support,
} from '@mui/icons-material';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';
import { ImageMosaic } from '../components/brand/ImageMosaic';

interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  image: string;
  category: string;
}

const services: Service[] = [
  {
    title: 'Custom Textile Manufacturing',
    description: 'Premium fabric production with sustainable materials and custom designs for fashion and industrial applications',
    icon: <ProductionQuantityLimits sx={{ fontSize: 40, color: '#6366f1' }} />,
    features: [
      'Custom fabric designs and patterns',
      'Sustainable and eco-friendly materials',
      'Advanced weaving and finishing techniques',
      'Quality assurance and testing',
      'Bulk production capabilities',
      'Fast turnaround times',
    ],
    image: BRAND.images.teamSign,
    category: 'Textiles',
  },
  {
    title: 'Smart Furniture Production',
    description: 'Innovative furniture manufacturing with integrated technology and ergonomic design principles',
    icon: <Build sx={{ fontSize: 40, color: BRAND.colors.navy }} />,
    features: [
      'Custom furniture design and prototyping',
      'Smart technology integration',
      'Ergonomic and sustainable materials',
      'Quality craftsmanship',
      'Modular and customizable options',
      'Professional installation services',
    ],
    image: BRAND.images.furniture,
    category: 'Furniture',
  },
  {
    title: 'Precision Metalworks',
    description: 'Advanced metal fabrication and precision engineering for industrial and commercial applications',
    icon: <PrecisionManufacturing sx={{ fontSize: 40, color: BRAND.colors.navyLight }} />,
    features: [
      'Custom metal fabrication',
      'Precision engineering and machining',
      'Quality control and testing',
      'Rapid prototyping services',
      'Industrial-grade materials',
      'Technical consultation',
    ],
    image: BRAND.images.facilityExterior,
    category: 'Metalworks',
  },
  {
    title: 'Supply Chain Solutions',
    description: 'End-to-end supply chain management and logistics services for efficient production and delivery',
    icon: <Storage sx={{ fontSize: 40, color: '#2e7d5a' }} />,
    features: [
      'Inventory management systems',
      'Supplier coordination and management',
      'Quality control throughout the chain',
      'Logistics and distribution',
      'Real-time tracking and monitoring',
      'Cost optimization strategies',
    ],
    image: BRAND.images.outdoorSign,
    category: 'Logistics',
  },
  {
    title: 'Quality Assurance Services',
    description: 'Comprehensive quality control and testing services ensuring excellence in every product',
    icon: <Verified sx={{ fontSize: 40, color: '#c45c26' }} />,
    features: [
      'Comprehensive quality testing',
      'International standards compliance',
      'Material and product certification',
      'Process optimization',
      'Continuous improvement programs',
      'Quality training and consulting',
    ],
    image: BRAND.images.teamFacility,
    category: 'Quality',
  },
  {
    title: 'Custom Manufacturing',
    description: 'Tailored manufacturing solutions designed to meet your specific business requirements',
    icon: <Settings sx={{ fontSize: 40, color: BRAND.colors.navy }} />,
    features: [
      'Custom product development',
      'Design consultation and prototyping',
      'Scalable production solutions',
      'Technical support and maintenance',
      'After-sales service',
      'Ongoing partnership support',
    ],
    image: BRAND.images.gateSign,
    category: 'Custom',
  },
];

const serviceCategories = [
  { name: 'All Services', value: 'all' },
  { name: 'Textiles', value: 'Textiles' },
  { name: 'Furniture', value: 'Furniture' },
  { name: 'Metalworks', value: 'Metalworks' },
  { name: 'Logistics', value: 'Logistics' },
  { name: 'Quality', value: 'Quality' },
  { name: 'Custom', value: 'Custom' },
];

export const ServicesPage: React.FC = () => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  return (
    <Box sx={{ bgcolor: BRAND.colors.creamLight, minHeight: '100vh' }}>
      <BrandHero
        title="Our Manufacturing Services"
        subtitle={BRAND.tagline}
        description="From custom textile production to precision metalworks and furniture — end-to-end manufacturing backed by our Monio Cluster facility and team."
        backgroundImage={BRAND.images.teamSign}
        align="center"
      />

      {/* Service Categories Filter */}
      <Container maxWidth="lg" sx={{ py: 6, pt: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Explore Our Services
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Filter by category to find the services that best match your needs
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            flexWrap="wrap"
          >
            {serviceCategories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                sx={{
                  background: selectedCategory === category.value 
                    ? 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)'
                      : 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Container>

      {/* Services Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {filteredServices.map((service, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={service.image}
                  alt={service.title}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {service.icon}
                    <Chip 
                      label={service.category} 
                      size="small" 
                      sx={{ 
                        ml: 'auto',
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        fontWeight: 500,
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  <List dense>
                    {service.features.map((feature, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      mt: 3,
                      border: '2px solid #6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        border: '2px solid #4f46e5',
                        background: 'rgba(99, 102, 241, 0.04)',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Our Services */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6, fontWeight: 700 }}>
            Why Choose Monio Services?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <AutoAwesome sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Innovation & Technology
                </Typography>
                <Typography color="text.secondary">
                  Cutting-edge manufacturing technology and innovative processes ensure superior quality and efficiency.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Speed sx={{ fontSize: 60, color: '#06b6d4', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Fast & Reliable
                </Typography>
                <Typography color="text.secondary">
                  Quick turnaround times and reliable delivery schedules to keep your business running smoothly.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Support sx={{ fontSize: 60, color: '#10b981', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Expert Support
                </Typography>
                <Typography color="text.secondary">
                  Dedicated support team and technical expertise to guide you through every step of the process.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Let's discuss how our manufacturing services can help your business grow
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  color: 'white',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Get a Quote
                </Typography>
                <Typography sx={{ mb: 3, opacity: 0.9 }}>
                  Request a custom quote for your manufacturing needs
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  Request Quote
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  color: 'white',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Schedule Consultation
                </Typography>
                <Typography sx={{ mb: 3, opacity: 0.9 }}>
                  Book a consultation with our manufacturing experts
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      border: '2px solid white',
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Book Consultation
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  color: 'white',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Contact Sales
                </Typography>
                <Typography sx={{ mb: 3, opacity: 0.9 }}>
                  Speak directly with our sales team
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  Contact Us
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};
